import type {
  ChapterPlan,
  Ledger,
  MomentumCorrectionOrder,
  MomentumEvaluationDimensions,
  NarrativeDensityAudit,
  NarrativeMotionContract,
  ProseControls,
  RepetitionContext,
  RepetitionEvidence,
  RepetitionReason,
  RepetitionSeverity,
  StoryBible,
} from '../types';

export const DEFAULT_PROSE_CONTROLS: ProseControls = {
  descriptionDensity: 'balanced',
  proceduralDetail: 'compressed',
  pacing: 'balanced',
  dialoguePreference: 'balanced',
};

const PROCEDURE_VERBS = [
  'check', 'checking', 'inspect', 'inspecting', 'test', 'testing', 'unload', 'unloading',
  'organize', 'organizing', 'clear', 'clearing', 'scrape', 'scraping', 'pump', 'pumping',
  'coordinate', 'coordinates', 'fence', 'maintenance', 'inventory', 'catalog', 'sort',
  'arrange', 'adjust', 'calibrate', 'measure', 'pack', 'unpack', 'load', 'loading',
];

const DISRUPTION_MARKERS = [
  'vibration', 'tremor', 'jolt', 'alarm', 'scream', 'attack', 'danger', 'threat',
  'discover', 'discovery', 'suddenly', 'rupture', 'collapse', 'explosion', 'ambush',
  'warning', 'underground', 'roar', 'shriek', 'interrupted', 'failed', 'failure',
  'betrayal', 'revelation', 'secret', 'dead', 'blood', 'gunshot', 'chase',
  'lurch', 'lurched', 'moving', 'unmanned', 'rogue', 'without a driver', 'noticed',
];

const ACTION_BEAT_VERBS = [
  'fall', 'fell', 'grab', 'grabbed', 'drag', 'dragged', 'run', 'ran', 'sprint',
  'dodge', 'duck', 'leap', 'jump', 'strike', 'swing', 'lunge', 'stumble',
];

const EQUIPMENT_NOUNS = [
  'respirator', 'compass', 'excavator', 'cart', 'pump', 'bolt', 'tool', 'tools',
  'supply', 'supplies', 'casing', 'filter', 'harness', 'rig', 'crate', 'pack',
  'rifle', 'blade', 'kit', 'gear', 'equipment',
];

const STATE_CHANGE_MARKERS = [
  'decided', 'decision', 'realized', 'learned', 'discovered', 'refused', 'agreed',
  'failed', 'succeeded', 'lost', 'gained', 'betrayed', 'revealed', 'changed',
  'could not', 'would not', 'must', 'never again', 'finally', 'at last',
];

export function getProseControls(bible?: StoryBible | null): ProseControls {
  return { ...DEFAULT_PROSE_CONTROLS, ...(bible?.proseControls || {}) };
}

export function getDisruptionDeadlinePercent(chapterNumber: number, pacing: ProseControls['pacing']): number {
  if (chapterNumber === 1) {
    return pacing === 'fast' ? 10 : pacing === 'deliberate' ? 20 : 15;
  }
  return pacing === 'fast' ? 15 : pacing === 'deliberate' ? 30 : 25;
}

export function getMaxSetupPercent(chapterNumber: number, pacing: ProseControls['pacing']): number {
  if (chapterNumber === 1) return pacing === 'deliberate' ? 25 : 20;
  return pacing === 'deliberate' ? 35 : 30;
}

export function getMaxRoutineProcedureBeats(proceduralDetail: ProseControls['proceduralDetail']): number {
  if (proceduralDetail === 'technical') return 8;
  if (proceduralDetail === 'moderate') return 4;
  return 2;
}

function inferViewpoint(plan: ChapterPlan): string {
  return plan.pov || plan.characterFocus?.[0] || 'viewpoint character';
}

function inferFromBeat(beat: string, field: 'goal' | 'obstacle' | 'decision' | 'consequence'): string {
  const lower = beat.toLowerCase();
  if (field === 'goal') {
    if (lower.includes('reach') || lower.includes('arrive')) return 'Reach the next objective safely';
    if (lower.includes('escape') || lower.includes('flee')) return 'Escape immediate danger';
    if (lower.includes('find') || lower.includes('discover')) return 'Learn what is hidden';
    return 'Advance the chapter objective without stalling';
  }
  if (field === 'obstacle') {
    if (lower.includes('threat') || lower.includes('attack') || lower.includes('danger')) return 'Active external threat';
    if (lower.includes('fail') || lower.includes('broken')) return 'Equipment or plan failure';
    return 'Opposition, risk, or uncertainty blocking easy progress';
  }
  if (field === 'decision') return 'Choose how to respond when pressure rises';
  if (field === 'consequence') return 'World, relationship, or objective state shifts because of the choice';
  return '';
}

export function buildNarrativeMotionContractsFromPlan(
  plan: ChapterPlan,
  chapterNumber: number,
  proseControls: ProseControls = DEFAULT_PROSE_CONTROLS,
): NarrativeMotionContract[] {
  const beats = plan.requiredBeats?.length
    ? plan.requiredBeats
    : [plan.summary || 'Advance the chapter objective'];
  const sceneCount = Math.min(4, Math.max(1, beats.length));
  const chunkSize = Math.ceil(beats.length / sceneCount);
  const viewpoint = inferViewpoint(plan);
  const disruptionDeadline = getDisruptionDeadlinePercent(chapterNumber, proseControls.pacing);
  const maxSetup = getMaxSetupPercent(chapterNumber, proseControls.pacing);
  const maxRoutine = getMaxRoutineProcedureBeats(proseControls.proceduralDetail);

  const contracts: NarrativeMotionContract[] = [];
  for (let i = 0; i < sceneCount; i++) {
    const sceneBeats = beats.slice(i * chunkSize, (i + 1) * chunkSize);
    const primaryBeat = sceneBeats[0] || plan.summary;
    const isOpeningScene = chapterNumber === 1 && i === 0;
    contracts.push({
      viewpointCharacter: viewpoint,
      immediateGoal: inferFromBeat(primaryBeat, 'goal'),
      meaningfulObstacle: inferFromBeat(primaryBeat, 'obstacle'),
      decisionRequired: inferFromBeat(primaryBeat, 'decision'),
      consequence: inferFromBeat(primaryBeat, 'consequence'),
      stateChange: sceneBeats.join(' → '),
      disruptionDeadlinePercent: isOpeningScene ? disruptionDeadline : Math.min(35, disruptionDeadline + 10),
      maxSetupPercent: isOpeningScene ? maxSetup : maxSetup + 10,
      maxRoutineProcedureBeats: maxRoutine,
      requiredEscalations: proseControls.pacing === 'fast' ? 2 : 1,
      sceneQuestion: `What changes because of: ${primaryBeat}?`,
      sceneAnswerOrComplication: sceneBeats[sceneBeats.length - 1] || plan.summary,
    });
  }
  return contracts;
}

export function buildNarrativeMotionPrompt(
  contracts: NarrativeMotionContract[],
  chapterNumber: number,
): string {
  return `
NARRATIVE MOTION CONTRACT (MANDATORY PER SCENE):
A scene must not exist solely to describe travel, equipment setup, or routine labor.
Every substantial scene must create at least one of: new danger, new information, changed relationship, irreversible decision, failed objective, increased cost, altered world state, meaningful emotional shift.

${chapterNumber === 1 ? `CHAPTER 1 OPENING RULE: The first meaningful disturbance or reader question must appear within the first ${contracts[0]?.disruptionDeadlinePercent ?? 15}% of the chapter. Do not spend hundreds of words cataloging objects before narrative tension appears.` : ''}

${contracts.map((c, idx) => `SCENE ${idx + 1}:
- Viewpoint: ${c.viewpointCharacter}
- Immediate goal: ${c.immediateGoal}
- Obstacle: ${c.meaningfulObstacle}
- Decision required: ${c.decisionRequired}
- Consequence: ${c.consequence}
- Required state change: ${c.stateChange}
- Disruption deadline: first ${c.disruptionDeadlinePercent}% of scene
- Max setup without disruption: ${c.maxSetupPercent}%
- Max routine procedure beats: ${c.maxRoutineProcedureBeats}
- Required escalations: ${c.requiredEscalations}
- Scene question: ${c.sceneQuestion}
- Answer/complication: ${c.sceneAnswerOrComplication}`).join('\n\n')}
`.trim();
}

export function buildProseControlPrompt(controls: ProseControls): string {
  const descriptionBudget =
    controls.descriptionDensity === 'lean'
      ? 'Lean description budget: one strong anchor detail per setting beat; no inventory cataloging.'
      : controls.descriptionDensity === 'lush'
        ? 'Lush description allowed only when attached to desire, danger, mystery, or conflict — never as object lists.'
        : 'Balanced description: prioritize details that reveal character, danger, atmosphere, foreshadowing, or later relevance.';

  const procedureRule =
    controls.proceduralDetail === 'compressed'
      ? 'Compress routine labor (unloading, organizing, checking ordinary equipment, repeated maintenance, standard travel). Summarize in one sentence, then dramatize only the consequential moment.'
      : controls.proceduralDetail === 'technical'
        ? 'Technical procedure detail is allowed when it changes meaning, reveals danger, or forces a decision — not for realism alone.'
        : 'Moderate procedure: brief setup allowed, but do not narrate every mechanical step.';

  const pacingRule =
    controls.pacing === 'fast'
      ? 'Fast pacing: enter scenes late, exit early; escalate quickly; minimize transitional labor.'
      : controls.pacing === 'deliberate'
        ? 'Deliberate pacing is allowed, but atmospheric description must still create tension or meaning — not delay the inciting disturbance.'
        : 'Balanced pacing: dramatize consequential moments; compress routine transitions.';

  const dialogueRule =
    controls.dialoguePreference === 'low'
      ? 'Use dialogue sparingly; prefer decisive action and internal conflict.'
      : controls.dialoguePreference === 'high'
        ? 'Use dialogue and character decisions to expand thin scenes instead of object description or procedural padding.'
        : 'Balance dialogue with action and internal conflict.';

  return `
PROJECT PROSE CONTROLS:
- Description density: ${controls.descriptionDensity} — ${descriptionBudget}
- Procedural detail: ${controls.proceduralDetail} — ${procedureRule}
- Pacing: ${controls.pacing} — ${pacingRule}
- Dialogue preference: ${controls.dialoguePreference} — ${dialogueRule}

DRAMATIZATION VS EXHAUSTIVE NARRATION:
Dramatize the consequential moments. Compress routine transitions and repeated labor.
"Show, don't tell" does NOT mean documenting every physical action.
Once an object is established, do not repeatedly redescribe its shape, mechanics, damage, movement, sound, color, or inventory unless the new description changes meaning or threat.
`.trim();
}

export function buildWordCountPolicyPrompt(controls: ProseControls): string {
  return `
WORD COUNT POLICY:
Prefer a shorter, complete, dramatically effective chapter over a longer chapter filled with repeated description or procedure.
Do NOT pad word count with: sensory inventory, equipment catalogs, internal monologue that repeats known information, extra travel, repeated attempts with no change, routine tasks.
Expansion MUST add: conflict, dialogue, meaningful character interaction, decisions, consequences, revelations, complications.
${controls.dialoguePreference !== 'low' ? 'If the draft is thin, expand through character decisions and dialogue — not environmental lists.' : ''}
`.trim();
}

export function buildInspectorMomentumAddendum(controls: ProseControls, chapterNumber: number): string {
  const disruptionThreshold = getDisruptionDeadlinePercent(chapterNumber, controls.pacing);
  const routineThreshold = controls.proceduralDetail === 'technical' ? 45 : 30;
  return `
NARRATIVE MOMENTUM INSPECTION (FAIL if padding blocks story motion; PASS deliberate atmospheric tension that creates meaning):

Answer explicitly:
1. What changes during this scene/chapter?
2. What does the viewpoint character want?
3. What prevents them from getting it?
4. What decision do they make?
5. What is different at the end?
6. Does the scene begin too early?
7. Does it end after its dramatic purpose is complete?
8. Is routine labor being narrated instead of compressed?
9. Are descriptions repeating information already established?
10. Does action continue after the outcome is already understandable?
11. Is the chapter meeting word count by adding detail rather than story?
12. Could 15–30% be removed without losing plot or character?

${chapterNumber === 1 ? `CHAPTER 1 CHECK: Does something happen in the first ${disruptionThreshold}% that creates a question the reader wants answered? If not, FAIL with a precise correction order.` : ''}

BLOCK (FAIL) for momentum padding when:
- First meaningful disruption occurs after ${disruptionThreshold + 10}%${chapterNumber === 1 ? ' (Chapter 1)' : ''} unless pacing is deliberate AND tension is created through atmosphere.
- Routine procedure exceeds ~${routineThreshold}% without consequential payoff (unless proceduralDetail is technical).
- Fewer than 2 meaningful state changes in a full chapter over 2000 words.
- Same object redescribed repeatedly without changed meaning.
- 3+ consecutive action beats without escalation in position, options, injury, knowledge, objective, threat, relationship, or time pressure.
- Two or more consecutive action beats could be merged without losing plot, character, or tension.

Correction orders must be specific (compress X sequence to Y words, move discovery earlier, remove repeated descriptions) — never vague "improve pacing."
`.trim();
}

function splitParagraphs(text: string): string[] {
  return text.split(/\n\s*\n+/).map(p => p.trim()).filter(Boolean);
}

function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

function countMatches(text: string, patterns: string[]): number {
  const lower = text.toLowerCase();
  return patterns.reduce((acc, p) => acc + (lower.match(new RegExp(`\\b${p}\\b`, 'g')) || []).length, 0);
}

function classifyParagraph(paragraph: string): {
  description: boolean;
  procedure: boolean;
  dialogue: boolean;
  internal: boolean;
  exposition: boolean;
  consequential: boolean;
} {
  const lower = paragraph.toLowerCase();
  const hasDialogue = /["“‘]/.test(paragraph);
  const procedureHits = countMatches(lower, PROCEDURE_VERBS);
  const disruptionHits = countMatches(lower, DISRUPTION_MARKERS);
  const stateHits = countMatches(lower, STATE_CHANGE_MARKERS);
  const equipmentHits = countMatches(lower, EQUIPMENT_NOUNS);
  const internal = /\b(i |he thought|she thought|wondered|remembered|felt that|knew that)\b/i.test(paragraph);
  const exposition = /\b(had been|years ago|once upon|history of|was known as|tradition)\b/i.test(paragraph);
  const adjectiveDensity = (paragraph.match(/\b\w+ly\b/g) || []).length + (paragraph.match(/,\s*\w+/g) || []).length;
  const description = !hasDialogue && (equipmentHits >= 2 || adjectiveDensity >= 4) && disruptionHits === 0;
  const procedure = procedureHits >= 2 && disruptionHits === 0 && stateHits === 0;
  const consequential = disruptionHits > 0 || stateHits > 0;
  return { description, procedure, dialogue: hasDialogue, internal, exposition, consequential };
}

export function detectRepeatedNouns(text: string, minCount = 5): string[] {
  const context = buildRepetitionContext();
  const findings = detectDescriptiveRepetition(text, context);
  const entityInfo = findings
    .filter((f) => f.severity === 'INFO' && f.reason === 'ENTITY_RECURRENCE')
    .map((f) => f.term);
  const legacyEquipment = detectLegacyEquipmentFrequency(text, minCount, context);
  return [...new Set([...entityInfo, ...legacyEquipment])];
}

/** Legacy equipment frequency for telemetry comparisons in tests — never used alone for blocking. */
function detectLegacyEquipmentFrequency(text: string, minCount: number, context: RepetitionContext): string[] {
  const words = text.toLowerCase().match(/\b[a-z]{4,}\b/g) || [];
  const counts = new Map<string, number>();
  for (const w of words) {
    counts.set(w, (counts.get(w) || 0) + 1);
  }
  return EQUIPMENT_NOUNS.filter((n) => {
    if ((counts.get(n) || 0) < minCount) return false;
    return !isExcludedEntityToken(n, context);
  });
}

const SENSORY_ADJECTIVES = new Set([
  'rusted', 'rusty', 'chrome', 'chromed', 'metallic', 'gleaming', 'glowing', 'shining',
  'dusty', 'sandy', 'broken', 'cracked', 'battered', 'scarred', 'dented', 'worn',
  'blue', 'red', 'cold', 'hot', 'warm', 'dark', 'bright', 'harsh', 'soft', 'rough',
  'heavy', 'thin', 'thick', 'sharp', 'dull', 'wet', 'dry', 'stale', 'bitter',
]);

function tokenizeSentence(sentence: string): string[] {
  return sentence
    .toLowerCase()
    .replace(/[^a-z0-9\s'-]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2);
}

function sentenceSimilarity(a: string, b: string): number {
  const stop = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'with', 'from', 'that', 'this', 'was', 'were', 'had', 'has']);
  const ta = new Set(tokenizeSentence(a).filter((w) => !stop.has(w)));
  const tb = new Set(tokenizeSentence(b).filter((w) => !stop.has(w)));
  if (ta.size === 0 || tb.size === 0) return 0;
  let inter = 0;
  for (const w of ta) if (tb.has(w)) inter += 1;
  return inter / (ta.size + tb.size - inter);
}

function extractCapitalizedTerms(text: string): string[] {
  return (text.match(/\b[A-Z][a-z]+(?:[''][a-z]+)?\b/g) || []).map((t) => t.toLowerCase());
}

export function buildRepetitionContext(
  plan?: ChapterPlan,
  bible?: StoryBible | null,
  ledger?: Ledger | null,
): RepetitionContext {
  const characterNames = new Set<string>();
  const aliases = new Set<string>();
  const locationNames = new Set<string>();
  const requiredEntities = new Set<string>();
  const criticalObjects = new Set<string>();

  if (plan?.pov) characterNames.add(plan.pov.toLowerCase());
  plan?.characterFocus?.forEach((c) => characterNames.add(c.toLowerCase()));
  plan?.locationFocus?.forEach((l) => locationNames.add(l.toLowerCase()));
  plan?.requiredBeats?.forEach((beat) => {
    extractCapitalizedTerms(beat).forEach((t) => requiredEntities.add(t));
    ['cart', 'pump', 'pipe', 'excavator', 'well', 'loader', 'mule', 'homestead', 'claim', 'ridge', 'cellar']
      .filter((o) => beat.toLowerCase().includes(o))
      .forEach((o) => criticalObjects.add(o));
  });

  Object.keys(ledger?.characters || {}).forEach((name) => {
    characterNames.add(name.toLowerCase());
    const parts = name.split(/\s+/);
    if (parts.length > 1) aliases.add(parts[parts.length - 1].toLowerCase());
  });

  if (bible?.title) requiredEntities.add(bible.title.toLowerCase());

  return {
    characterNames: [...characterNames],
    aliases: [...aliases],
    locationNames: [...locationNames],
    factionNames: [],
    requiredEntities: [...requiredEntities],
    criticalObjects: [...criticalObjects],
  };
}

export function isExcludedEntityToken(token: string, context: RepetitionContext): boolean {
  const lower = token.toLowerCase();
  if (context.characterNames.includes(lower)) return true;
  if (context.aliases.includes(lower)) return true;
  if (context.locationNames.some((l) => lower.includes(l) || l.includes(lower))) return true;
  if (context.requiredEntities.includes(lower)) return true;
  if (context.criticalObjects.includes(lower)) return true;
  return false;
}

function extractAttributePhrases(sentence: string, context: RepetitionContext): string[] {
  const phrases: string[] = [];
  const regex = /\b([\w'-]+)\s+([\w'-]+)\b/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(sentence)) !== null) {
    const first = match[1].toLowerCase();
    const second = match[2].toLowerCase();
    if (SENSORY_ADJECTIVES.has(first) && !isExcludedEntityToken(second, context)) {
      phrases.push(`${first} ${second}`);
    }
    if (SENSORY_ADJECTIVES.has(second) && !isExcludedEntityToken(first, context)) {
      phrases.push(`${second} ${first}`);
    }
  }
  return phrases;
}

function indexSentences(text: string): { sentence: string; paragraphIndex: number; sentenceIndex: number }[] {
  const paragraphs = splitParagraphs(text);
  const indexed: { sentence: string; paragraphIndex: number; sentenceIndex: number }[] = [];
  paragraphs.forEach((p, paragraphIndex) => {
    const sentences = p.split(/[.!?]+/).map((s) => s.trim()).filter(Boolean);
    sentences.forEach((sentence, sentenceIndex) => {
      indexed.push({ sentence, paragraphIndex, sentenceIndex });
    });
  });
  return indexed;
}

export function detectDescriptiveRepetition(text: string, context: RepetitionContext): RepetitionEvidence[] {
  const findings: RepetitionEvidence[] = [];
  const indexed = indexSentences(text);
  const paragraphs = splitParagraphs(text);

  // Entity recurrence → INFO only
  const wordCounts = new Map<string, number>();
  for (const w of text.toLowerCase().match(/\b[a-z]{3,}\b/g) || []) {
    wordCounts.set(w, (wordCounts.get(w) || 0) + 1);
  }
  for (const [token, count] of wordCounts.entries()) {
    if (!isExcludedEntityToken(token, context)) continue;
    if (count >= 8) {
      findings.push({
        term: token,
        excerpts: [],
        paragraphIndexes: [],
        reason: 'ENTITY_RECURRENCE',
        severity: 'INFO',
      });
    }
  }

  // Near-duplicate sentences within local window
  const LOCAL_SENTENCE_WINDOW = 4;
  const DUPLICATE_THRESHOLD = 0.65;
  for (let i = 0; i < indexed.length; i++) {
    for (let j = i + 1; j < Math.min(indexed.length, i + LOCAL_SENTENCE_WINDOW); j++) {
      const a = indexed[i].sentence;
      const b = indexed[j].sentence;
      const sim = sentenceSimilarity(a, b);
      const aOpen = tokenizeSentence(a).slice(0, 3).join(' ');
      const bOpen = tokenizeSentence(b).slice(0, 3).join(' ');
      const structuralDup = aOpen.length > 5 && aOpen === bOpen && sim >= 0.5;
      if ((sim >= DUPLICATE_THRESHOLD || structuralDup) && a.length > 25) {
        findings.push({
          term: `sentence-pair-${i}-${j}`,
          excerpts: [a.slice(0, 160), b.slice(0, 160)],
          paragraphIndexes: [indexed[i].paragraphIndex, indexed[j].paragraphIndex],
          similarityScore: Math.round(sim * 100) / 100,
          reason: 'NEAR_DUPLICATE_SENTENCE',
          severity: 'BLOCKING',
        });
      }
    }
  }

  // Repeated adjective+noun (descriptive) within paragraph or 5-sentence window
  const phraseHits = new Map<string, { excerpts: string[]; paragraphIndexes: Set<number> }>();
  indexed.forEach((entry, idx) => {
    const phrases = extractAttributePhrases(entry.sentence, context);
    for (const phrase of phrases) {
      const bucket = phraseHits.get(phrase) || { excerpts: [], paragraphIndexes: new Set<number>() };
      bucket.excerpts.push(entry.sentence.slice(0, 140));
      bucket.paragraphIndexes.add(entry.paragraphIndex);
      phraseHits.set(phrase, bucket);
      // local window count
      const windowPhrases = indexed
        .slice(Math.max(0, idx - 4), idx + 1)
        .flatMap((e) => extractAttributePhrases(e.sentence, context));
      const localCount = windowPhrases.filter((p) => p === phrase).length;
      if (localCount >= 3) {
        findings.push({
          term: phrase,
          excerpts: bucket.excerpts.slice(-3),
          paragraphIndexes: [...bucket.paragraphIndexes],
          reason: 'REPEATED_ATTRIBUTE',
          severity: 'BLOCKING',
        });
      }
    }
  });
  for (const [phrase, bucket] of phraseHits.entries()) {
    if (bucket.excerpts.length >= 3 && bucket.paragraphIndexes.size >= 2) {
      const exists = findings.some((f) => f.term === phrase && f.reason === 'REPEATED_ATTRIBUTE');
      if (!exists) {
        findings.push({
          term: phrase,
          excerpts: bucket.excerpts.slice(0, 3),
          paragraphIndexes: [...bucket.paragraphIndexes],
          reason: 'REDUNDANT_SENSORY_DESCRIPTION',
          severity: 'BLOCKING',
        });
      }
    }
  }

  // Repeated mechanical explanation in close proximity
  for (let p = 0; p < paragraphs.length; p++) {
    const lower = paragraphs[p].toLowerCase();
    const procHits = PROCEDURE_VERBS.filter((v) => new RegExp(`\\b${v}\\b`).test(lower)).length;
    const equipHits = EQUIPMENT_NOUNS.filter((n) => new RegExp(`\\b${n}\\b`).test(lower) && !isExcludedEntityToken(n, context)).length;
    if (procHits >= 2 && equipHits >= 2 && !classifyParagraph(paragraphs[p]).consequential) {
      const nearDup = paragraphs
        .slice(Math.max(0, p - 1), p + 2)
        .filter((para) => {
          const l = para.toLowerCase();
          return PROCEDURE_VERBS.some((v) => l.includes(v)) && EQUIPMENT_NOUNS.some((n) => l.includes(n));
        });
      if (nearDup.length >= 2) {
        findings.push({
          term: 'mechanical-sequence',
          excerpts: nearDup.map((para) => para.slice(0, 140)),
          paragraphIndexes: [p],
          reason: 'REPEATED_MECHANICAL_EXPLANATION',
          severity: 'BLOCKING',
        });
      }
    }
  }

  // Deduplicate blocking findings by term+reason
  const seen = new Set<string>();
  return findings.filter((f) => {
    const key = `${f.reason}:${f.term}:${f.severity}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function detectEntityOnlyFrequencyBlock(text: string, context: RepetitionContext): string[] {
  /** Bare token-frequency list — must never produce blocking terms. */
  const words = text.toLowerCase().match(/\b[a-z]{3,}\b/g) || [];
  const counts = new Map<string, number>();
  for (const w of words) counts.set(w, (counts.get(w) || 0) + 1);
  return [...counts.entries()]
    .filter(([w, c]) => c >= 5 && isExcludedEntityToken(w, context))
    .map(([w]) => w);
}

export function detectRepeatedActionClusters(text: string): string[] {
  const sentences = text.split(/[.!?]+/).map(s => s.trim()).filter(Boolean);
  const clusters: string[] = [];
  for (let i = 0; i < sentences.length - 2; i++) {
    const window = sentences.slice(i, i + 3).join(' ').toLowerCase();
    const verbsFound = ACTION_BEAT_VERBS.filter(v => new RegExp(`\\b${v}\\b`).test(window));
    const uniqueVerbs = new Set(verbsFound);
    if (uniqueVerbs.size >= 2 && verbsFound.length >= 3) {
      clusters.push(sentences.slice(i, i + 3).join(' | ').slice(0, 120));
    }
  }
  return [...new Set(clusters)].slice(0, 5);
}

export function detectLowValueProcedureSequences(text: string): string[] {
  const paragraphs = splitParagraphs(text);
  const sequences: string[] = [];
  let run: string[] = [];
  for (const p of paragraphs) {
    const cls = classifyParagraph(p);
    if (cls.procedure && !cls.consequential) {
      run.push(p.slice(0, 80));
    } else if (run.length >= 2) {
      sequences.push(run.join(' / '));
      run = [];
    } else {
      run = [];
    }
  }
  if (run.length >= 2) sequences.push(run.join(' / '));
  return sequences;
}

export function findFirstDisruptionPositionPercent(text: string): number {
  const total = wordCount(text);
  if (total === 0) return 100;
  const lower = text.toLowerCase();
  let earliest = total;
  for (const marker of DISRUPTION_MARKERS) {
    const idx = lower.indexOf(marker);
    if (idx >= 0) {
      const wordsBefore = text.slice(0, idx).split(/\s+/).filter(Boolean).length;
      earliest = Math.min(earliest, (wordsBefore / total) * 100);
    }
  }
  return earliest === total ? 100 : Math.round(earliest * 10) / 10;
}

function findPrimaryDisruptionPositionPercent(text: string): number {
  const primaryMarkers = [
    'vibration', 'tremor', 'underground', 'discovery', 'revelation', 'betrayal',
    'attack', 'ambush', 'gunshot', 'explosion', 'danger', 'threat', 'lurch', 'lurched',
    'moving', 'unmanned', 'without a driver',
  ];
  const total = wordCount(text);
  if (total === 0) return 100;
  const lower = text.toLowerCase();
  let earliest = total;
  for (const marker of primaryMarkers) {
    const idx = lower.indexOf(marker);
    if (idx >= 0) {
      const wordsBefore = text.slice(0, idx).split(/\s+/).filter(Boolean).length;
      earliest = Math.min(earliest, (wordsBefore / total) * 100);
    }
  }
  return earliest === total ? 100 : Math.round(earliest * 10) / 10;
}

export function countMeaningfulStateChanges(text: string): number {
  const paragraphs = splitParagraphs(text);
  return paragraphs.filter(p => classifyParagraph(p).consequential).length;
}

export function auditNarrativeDensity(
  text: string,
  chapterNumber = 1,
  proseControls: ProseControls = DEFAULT_PROSE_CONTROLS,
  plan?: ChapterPlan,
  bible?: StoryBible | null,
  ledger?: Ledger | null,
): NarrativeDensityAudit {
  const paragraphs = splitParagraphs(text);
  const totalWords = wordCount(text);
  const repetitionContext = buildRepetitionContext(plan, bible, ledger);
  const repetitionFindings = detectDescriptiveRepetition(text, repetitionContext);
  const blockingRepetition = repetitionFindings.filter((f) => f.severity === 'BLOCKING');
  const warningRepetition = repetitionFindings.filter((f) => f.severity === 'WARNING');
  const repetitionSeverity: NarrativeDensityAudit['repetitionSeverity'] =
    blockingRepetition.length > 0 ? 'BLOCKING' : warningRepetition.length > 0 ? 'WARNING' : 'PASS';

  let descriptionWords = 0;
  let procedureWords = 0;
  let dialogueWords = 0;
  let internalWords = 0;
  let expositionWords = 0;
  let consequentialWords = 0;

  for (const p of paragraphs) {
    const wc = wordCount(p);
    const cls = classifyParagraph(p);
    if (cls.dialogue) dialogueWords += wc;
    if (cls.internal) internalWords += wc;
    if (cls.exposition) expositionWords += wc;
    if (cls.description) descriptionWords += wc;
    if (cls.procedure) procedureWords += wc;
    if (cls.consequential) consequentialWords += wc;
  }

  const pct = (n: number) => (totalWords > 0 ? Math.round((n / totalWords) * 1000) / 10 : 0);

  return {
    descriptionPercent: pct(descriptionWords),
    routineProcedurePercent: pct(procedureWords),
    dialoguePercent: pct(dialogueWords),
    internalConflictPercent: pct(internalWords),
    consequentialActionPercent: pct(consequentialWords),
    expositionPercent: pct(expositionWords),
    firstDisruptionPositionPercent: findFirstDisruptionPositionPercent(text),
    meaningfulStateChanges: countMeaningfulStateChanges(text),
    repeatedActionClusters: detectRepeatedActionClusters(text),
    overdescribedObjects: detectRepeatedNouns(text, proseControls.descriptionDensity === 'lean' ? 4 : 5),
    repetitionFindings,
    repetitionSeverity,
    lowValueProcedureSequences: detectLowValueProcedureSequences(text),
    warnings: [],
    chapterNumber,
  };
}

function pushWarning(audit: NarrativeDensityAudit, msg: string) {
  audit.warnings = audit.warnings || [];
  audit.warnings.push(msg);
}

export function evaluateChapterMomentum(
  text: string,
  chapterNumber: number,
  plan: ChapterPlan | undefined,
  proseControls: ProseControls = DEFAULT_PROSE_CONTROLS,
  bible?: StoryBible | null,
  ledger?: Ledger | null,
): {
  shouldBlock: boolean;
  shouldBlockMomentum: boolean;
  shouldBlockRepetition: boolean;
  dimensions: MomentumEvaluationDimensions;
  audit: NarrativeDensityAudit;
  correction: MomentumCorrectionOrder;
  reason: string;
} {
  const audit = auditNarrativeDensity(text, chapterNumber, proseControls, plan, bible, ledger);
  const disruptionDeadline = getDisruptionDeadlinePercent(chapterNumber, proseControls.pacing);
  const primaryDisruption = findPrimaryDisruptionPositionPercent(text);
  const effectiveDisruption =
    chapterNumber === 1
      ? Math.min(audit.firstDisruptionPositionPercent, primaryDisruption)
      : audit.firstDisruptionPositionPercent;
  const routineCap = proseControls.proceduralDetail === 'technical' ? 45 : 30;
  const correction: MomentumCorrectionOrder = { requiredRevision: '' };
  const momentumReasons: string[] = [];
  const repetitionReasons: string[] = [];

  const disruptionGrace = proseControls.pacing === 'deliberate' ? 30 : 10;

  if (effectiveDisruption > disruptionDeadline + disruptionGrace) {
    if (chapterNumber === 1 && primaryDisruption <= disruptionDeadline + 5) {
      pushWarning(audit, `Opening threat at ${primaryDisruption}% precedes later payoff at ${audit.firstDisruptionPositionPercent}%.`);
    } else if (chapterNumber === 1 || proseControls.pacing !== 'deliberate') {
      correction.delayedDisruption = true;
      momentumReasons.push(
        `First meaningful disruption at ${effectiveDisruption}% (threshold ~${disruptionDeadline}% for Chapter ${chapterNumber}).`,
      );
    } else {
      pushWarning(audit, `Late first disruption (${effectiveDisruption}%) allowed for deliberate pacing.`);
    }
  }

  if (audit.routineProcedurePercent > routineCap && proseControls.proceduralDetail === 'compressed') {
    correction.proceduralPadding = audit.lowValueProcedureSequences.length
      ? audit.lowValueProcedureSequences
      : [`Routine procedure density ${audit.routineProcedurePercent}%`];
    momentumReasons.push(`Routine procedure ~${audit.routineProcedurePercent}% exceeds ~${routineCap}% cap.`);
  }

  if (wordCount(text) > 2000 && audit.meaningfulStateChanges < 2) {
    correction.weakSceneChange = ['Fewer than 2 meaningful state changes in chapter'];
    momentumReasons.push(`Only ${audit.meaningfulStateChanges} meaningful state changes detected.`);
  }

  const blockingRepetition = (audit.repetitionFindings || []).filter((f) => f.severity === 'BLOCKING');
  const warningRepetition = (audit.repetitionFindings || []).filter((f) => f.severity === 'WARNING');
  if (blockingRepetition.length > 0 && proseControls.descriptionDensity !== 'lush') {
    correction.excessiveDescription = blockingRepetition.map((f) => f.term);
    correction.repetitionEvidence = blockingRepetition;
    correction.targetedLineEdits = blockingRepetition.flatMap((f) => f.excerpts).slice(0, 4);
    repetitionReasons.push(
      ...blockingRepetition.map((f) => `${f.reason} (${f.term}): ${f.excerpts[0]?.slice(0, 80) || 'see excerpts'}`),
    );
  } else if (warningRepetition.length > 0) {
    pushWarning(audit, `Descriptive repetition warning: ${warningRepetition.map((f) => f.term).join(', ')}`);
  }

  const entityInfo = (audit.repetitionFindings || []).filter((f) => f.severity === 'INFO');
  if (entityInfo.length > 0) {
    pushWarning(
      audit,
      `Recurring entities (expected): ${entityInfo.map((f) => f.term).join(', ')}`,
    );
  }

  if (audit.repeatedActionClusters.length > 0) {
    const primaryDisruptionPos = findPrimaryDisruptionPositionPercent(text);
    const allowDeliberateAction =
      proseControls.pacing === 'deliberate' && primaryDisruptionPos <= getDisruptionDeadlinePercent(chapterNumber, proseControls.pacing) + 10;
    if (!allowDeliberateAction) {
      correction.repeatedAction = audit.repeatedActionClusters;
      momentumReasons.push('Repeated action beats without clear escalation.');
    } else {
      pushWarning(audit, 'Repeated action phrasing detected; allowed under deliberate pacing with early disruption.');
    }
  }

  if (audit.firstDisruptionPositionPercent > 25) {
    pushWarning(audit, `First disruption after 25% (${audit.firstDisruptionPositionPercent}%).`);
  }
  if (audit.routineProcedurePercent > 30 && proseControls.proceduralDetail !== 'technical') {
    pushWarning(audit, `Routine procedure ${audit.routineProcedurePercent}% exceeds 30% guideline.`);
  }

  const shouldBlockMomentum = momentumReasons.length > 0;
  const shouldBlockRepetition = repetitionReasons.length > 0;
  const shouldBlock = shouldBlockMomentum || shouldBlockRepetition;

  if (shouldBlock) {
    correction.requiredRevision = buildRequiredRevision(correction, audit, chapterNumber);
  }

  const dimensions: MomentumEvaluationDimensions = {
    continuity: 'PASS',
    momentum: shouldBlockMomentum ? 'FAIL' : momentumReasons.length === 0 && (audit.warnings?.length || 0) > 0 ? 'WARNING' : 'PASS',
    repetition:
      shouldBlockRepetition ? 'FAIL' : audit.repetitionSeverity === 'WARNING' ? 'WARNING' : 'PASS',
    overall: shouldBlock ? 'FAIL' : 'PASS',
  };

  return {
    shouldBlock,
    shouldBlockMomentum,
    shouldBlockRepetition,
    dimensions,
    audit,
    correction,
    reason: [...momentumReasons, ...repetitionReasons].join(' '),
  };
}

export function buildRequiredRevision(
  order: MomentumCorrectionOrder,
  audit: NarrativeDensityAudit,
  chapterNumber: number,
): string {
  const parts: string[] = [];
  if (order.delayedDisruption) {
    parts.push(
      `Move the first meaningful disturbance or reader question into the first ${chapterNumber === 1 ? '10–20' : '25'}% of the chapter.`,
    );
  }
  if (order.proceduralPadding?.length) {
    parts.push(
      `Compress routine labor sequences (${order.proceduralPadding.slice(0, 2).join('; ')}). Summarize maintenance/travel in one sentence; dramatize only failure, discovery, danger, or decision moments.`,
    );
    order.targetReductionPercent = order.targetReductionPercent || 20;
  }
  if (order.excessiveDescription?.length) {
    const evidenceLines = (order.repetitionEvidence || [])
      .flatMap((e) => e.excerpts.map((ex) => `"${ex.slice(0, 100)}..."`))
      .slice(0, 3);
    parts.push(
      evidenceLines.length
        ? `Remove redundant descriptive phrasing at: ${evidenceLines.join(' | ')}`
        : `Remove repeated descriptions of: ${order.excessiveDescription.join(', ')}. Keep one establishing detail per object unless meaning changes.`,
    );
  }
  if (order.targetedLineEdits?.length) {
    parts.push(`Targeted line edits only (do not full-rewrite): compress or vary these passages — ${order.targetedLineEdits.slice(0, 2).join(' | ')}`);
  }
  if (order.repeatedAction?.length) {
    parts.push(
      `Merge or compress repeated action beats (${order.repeatedAction[0]}). Each beat must change position, options, injury, knowledge, objective, threat, relationship, or time pressure.`,
    );
  }
  if (order.weakSceneChange?.length) {
    parts.push('Add at least two scenes/beats with clear state change: new danger, information, relationship shift, decision, failure, cost, or emotional turn.');
  }
  if (order.targetReductionPercent) {
    parts.push(`Target ~${order.targetReductionPercent}% reduction in padding without losing plot or character.`);
  }
  return parts.join(' ') || 'Revise for narrative momentum: compress routine labor and redundant description; escalate story motion.';
}

export function formatMomentumCorrectionOrder(order: MomentumCorrectionOrder): string {
  const lines = ['=== NARRATIVE MOMENTUM CORRECTION ORDER ==='];
  if (order.delayedDisruption) lines.push('- DELAYED DISRUPTION: Move inciting disturbance earlier.');
  if (order.excessiveDescription?.length) {
    const evidence = order.repetitionEvidence?.[0];
    if (evidence?.excerpts?.length) {
      lines.push(`- EXCESSIVE DESCRIPTION (${evidence.reason}): ${evidence.excerpts.slice(0, 2).join(' | ')}`);
    } else {
      lines.push(`- EXCESSIVE DESCRIPTION: ${order.excessiveDescription.join(', ')}`);
    }
  }
  if (order.targetedLineEdits?.length) {
    lines.push(`- TARGETED LINE EDITS: ${order.targetedLineEdits.slice(0, 2).join(' | ')}`);
  }
  if (order.proceduralPadding?.length) {
    lines.push(`- PROCEDURAL PADDING: ${order.proceduralPadding.join(' | ')}`);
  }
  if (order.repeatedAction?.length) {
    lines.push(`- REPEATED ACTION: ${order.repeatedAction.join(' | ')}`);
  }
  if (order.weakSceneChange?.length) {
    lines.push(`- WEAK SCENE CHANGE: ${order.weakSceneChange.join('; ')}`);
  }
  if (order.lateSceneStart?.length) {
    lines.push(`- LATE SCENE START: ${order.lateSceneStart.join('; ')}`);
  }
  if (order.extendedSceneEnding?.length) {
    lines.push(`- EXTENDED SCENE ENDING: ${order.extendedSceneEnding.join('; ')}`);
  }
  lines.push('', `REQUIRED REVISION: ${order.requiredRevision}`);
  if (order.targetReductionPercent) {
    lines.push(`TARGET REDUCTION: ~${order.targetReductionPercent}% padding removal`);
  }
  return lines.join('\n');
}

export function sceneHasMeaningfulStateChange(sceneText: string): boolean {
  return classifyParagraph(sceneText).consequential || countMeaningfulStateChanges(sceneText) >= 1;
}

export function buildExpansionMomentumGuard(controls: ProseControls): string {
  return `
EXPANSION MOMENTUM GUARD:
Expand through conflict, dialogue, decisions, consequences, revelations, and complications — NOT through sensory inventory, equipment catalogs, routine labor, or repeated internal monologue.
${controls.proceduralDetail === 'compressed' ? 'Do not expand by narrating every mechanical step of routine tasks.' : ''}
${controls.descriptionDensity === 'lean' ? 'Keep description lean while expanding story motion.' : ''}
`.trim();
}
